import { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  styled,
  Tooltip,
  Divider,
  useTheme,
} from '@mui/material';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { nimbleConnectUrlFlag } from 'utils/constants/urls';
import { candidatePortalRedirectUrls } from 'utils/constants/urls';
import { getConnectUrlPath } from 'features/Connect/features/JobDiscovery/utils';
import { handleOpenUserMenu, handleCloseUserMenu } from './utils';
import { candidateMenuOptions } from './constants';
import {
  CandidateNavbarUserDropdownMenuProps,
  NotificationParams,
  NotificationData,
} from '../types';
import { CandidateNavbarDataTestIds } from 'data-testids/general';
import auth from 'utils/auth';

export const CandidateNavbarUserDropdownMenu: React.FC<CandidateNavbarUserDropdownMenuProps> = ({
  user,
  candidateMatches,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const history = useHistory();
  const isOnConnect = useRouteMatch({ path: `/${nimbleConnectUrlFlag}` });
  const jobBoardState = useStateParam();
  const connectUrlPath = getConnectUrlPath(jobBoardState);
  const { connectContextActions } = useConnectContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const atMatchDashboard = useRouteMatch({ path: candidatePortalRedirectUrls.CANDIDATE_MATCHES });

  const mainMenuItems = candidateMenuOptions.filter((option) => option.menuSection === 'main');
  const footerMenuItems = candidateMenuOptions.filter((option) => option.menuSection === 'footer');
  const MenuActionParams = {
    isOnConnect: !!isOnConnect,
    userType: user.profile.user_type,
    jobBoardState,
    connectContextActions,
    history,
    connectUrlPath,
  };

  const notificationParams: NotificationParams = {
    atMatchDashboard: !!atMatchDashboard,
    isOnConnect: !!isOnConnect,
  };

  const getBadgeContent = () => {
    let totalCount = 0;

    // Check all menu items for notifications
    [...mainMenuItems, ...footerMenuItems].forEach((option) => {
      if (option.notification) {
        const { config } = option.notification;
        const notificationData: NotificationData = {
          candidateMatches,
        };
        const count = config.getData(notificationData);
        const shouldShow = config.shouldShow(notificationParams);
        if (shouldShow && count > 0) {
          totalCount += count;
        }
      }
    });

    return totalCount;
  };

  const renderMenuLabel = (option: (typeof candidateMenuOptions)[0]) => {
    if (typeof option.label === 'function' && option.notification) {
      const { config } = option.notification;
      const notificationData: NotificationData = {
        candidateMatches,
      };
      const count = config.getData(notificationData);
      const shouldShow = config.shouldShow(notificationParams);
      return option.label({
        count,
        shouldShowNotification: shouldShow && count > 0,
        context: 'navbar',
      });
    }
    return option.label;
  };

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Open settings">
        <IconButton
          onClick={(e) => handleOpenUserMenu(e, setAnchorEl)}
          sx={{ p: 0 }}
          data-testid={CandidateNavbarDataTestIds.USER_MENU}
        >
          <Badge
            badgeContent={getBadgeContent()}
            color="primary"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            invisible={!getBadgeContent()}
            data-testid={CandidateNavbarDataTestIds.NOTIFICATION_BADGE}
          >
            <Avatar alt={user?.first_name} src="placeholder" />
          </Badge>
        </IconButton>
      </Tooltip>
      <StyledMenu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={() => handleCloseUserMenu(setAnchorEl)}
        sx={{
          marginTop: isMobile ? theme.spacing(2) : '45px',
        }}
      >
        {mainMenuItems.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => {
              handleCloseUserMenu(setAnchorEl);
              option.menuItemAction(MenuActionParams);
            }}
            data-testid={option.dataTestId}
          >
            <MenuItemTypography data-testid={`${option.dataTestId}-label`}>
              {renderMenuLabel(option)}
            </MenuItemTypography>
          </MenuItem>
        ))}
        <Box sx={{ my: 1 }}>
          <Divider />
        </Box>
        {footerMenuItems.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => {
              handleCloseUserMenu(setAnchorEl);
              option.menuItemAction(MenuActionParams);
            }}
            data-testid={option.dataTestId}
          >
            <MenuItemTypography data-testid={`${option.dataTestId}-label`}>
              {renderMenuLabel(option)}
            </MenuItemTypography>
          </MenuItem>
        ))}
        {user.is_hijacked && (
          <MenuItem
            data-testid={CandidateNavbarDataTestIds.USER_MENU_RELEASE_HIJACK}
            onClick={() => {
              handleCloseUserMenu(setAnchorEl);
              auth.handleReleaseHijack();
            }}
          >
            <MenuItemTypography
              data-testid={`${CandidateNavbarDataTestIds.USER_MENU_RELEASE_HIJACK}-label`}
            >
              Release Hijack
            </MenuItemTypography>
          </MenuItem>
        )}
      </StyledMenu>
    </Box>
  );
};

const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    minWidth: '164px',
  },
});

const MenuItemTypography = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  fontSize: theme.typography.subtitle.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightRegular,
  lineHeight: '28px',
}));
